/* What presets used to be: named (voice, model, settings) triples in this
   browser's localStorage.

   They are rows on the server now — shared by every panel on the instance,
   editable only by an admin, and each one able to own an OBS browser source of
   its own. What is left here is the one-way door out of the old world: an
   admin's stored shortcuts are offered for import once, and then this file has
   nothing more to do.

   Kept rather than deleted because the alternative is a streamer opening the
   panel after a deploy to find nine carefully-tuned characters gone. */

import { loadPrefs, savePrefs } from "./prefs";
import type { VoiceSettings } from "./types";

export interface LegacyPreset {
  id: string;
  name: string;
  voice_id: string;
  model_id: string;
  settings: VoiceSettings;
}

/** Presets this browser stored before they moved to the server. */
export function loadLegacyPresets(): LegacyPreset[] {
  const stored = loadPrefs().presets;
  if (!Array.isArray(stored)) return [];
  return stored.filter(isLegacyPreset);
}

/** Forget them, once they have been offered — imported or declined. Leaving
    them behind would mean asking again on every reload. */
export function clearLegacyPresets(): void {
  savePrefs({ presets: [] });
}

/** Rejects anything that would be sent to the server as a broken preset —
    localStorage is shared with whatever an older build wrote there. */
function isLegacyPreset(value: unknown): value is LegacyPreset {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<LegacyPreset>;
  return (
    typeof candidate.name === "string" &&
    typeof candidate.voice_id === "string" &&
    typeof candidate.model_id === "string" &&
    typeof candidate.settings === "object" &&
    candidate.settings !== null
  );
}
